import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { newIdempotencyKey } from '../client/idempotency';
import { queryKeys } from '../client/query-client';
import { ChallengeDay } from './journey';
/**
 * The weekly challenge's writes and its leaderboard (spec §10).
 */
/**
 * Soft and anonymous: how you compare, never who is above you.
 *
 * `percentileBand` is rounded to the nearest ten so nobody is a rank. This is
 * deliberate and not negotiable — "this is a platform for parents who are
 * struggling, and being 47th out of 50 at parenting is the opposite of the
 * intended effect." It is also why the design's participant avatars are not
 * built: they are the same disclosure the banding exists to prevent.
 */
/**
 * One band of "Your circle" — `LeaderboardBand` in the contract.
 *
 * `{ band, participants }` and nothing else. The admin surface's
 * `ChallengeBandCount` carries a `label`; the backend deliberately does NOT
 * send it here, because those labels are three hardcoded English literals with
 * no translation table behind them — permanently wrong under a French heading.
 * Our copy is keyed off the enum in `@lb/i18n`.
 *
 * Order is fixed and meaningful — best to least — so render in the order
 * received rather than sorting.
 */
export const ChallengeBand = z.enum(['EVERY_DAY', 'MOST_DAYS', 'FINDING_RHYTHM']);
export const LeaderboardBand = z.object({
    band: ChallengeBand,
    participants: z.number().int().nonnegative(),
});
export const Leaderboard = z.object({
    participants: z.number().int().nonnegative(),
    yourStreak: z.number().int().nonnegative(),
    medianStreak: z.number().int().nonnegative(),
    percentileBand: z.number().int().min(0).max(100).nullable(),
    /**
     * NULLABLE, not optional — the field is always present.
     *
     * `null` means no challenge period is live, and that is the case that falls
     * back to the summary line. `[]` is NOT used for that: a running period with
     * nobody in it returns all three bands at zero, because a bar that vanishes
     * when empty makes the chart mean something different week to week.
     *
     * `nullish` rather than `nullable` ONLY for the rollout: the contract
     * guarantees the field is present, but a server that has not been redeployed
     * since round 10 omits it entirely — staging does today — and a required
     * field would take the whole leaderboard down rather than one chart. Tighten
     * to `.nullable()` once staging carries it.
     */
    bands: z.array(LeaderboardBand).nullish(),
    /**
     * Which band the caller is in — shipped round 11.
     *
     * NOT derived from `yourStreak`, and the backend's own correction is why: the
     * band is participation inside the LIVE PERIOD, the streak is a rolling
     * 120-day window. A parent on a 40-day streak who logged three of five
     * elapsed days sits in `MOST_DAYS`, and deriving it would have highlighted
     * `EVERY_DAY` — wrong, and looking right.
     *
     * `nullish` for the same rollout reason as `bands`: a server that predates
     * this field omits it, and a required field would take the chart down.
     */
    yourBand: ChallengeBand.nullish(),
});
export function useLeaderboard(http) {
    return useQuery({
        queryKey: queryKeys.journey.leaderboard,
        queryFn: () => http.get('/journey/challenge/leaderboard', { schema: Leaderboard }),
        retry: false,
    });
}
/**
 * Log a day.
 *
 * `day` is optional so today is the default and backfill is one field. The
 * server bounds backfill to yesterday — unbounded, anyone fills a hundred days
 * and reaches Evergreen in one request; refused entirely, it punishes the
 * parent who did the thing and forgot to tap.
 */
/**
 * `{ day: ChallengeDay }` — an envelope whose key collides with a real field.
 *
 * `ChallengeDay` has a `day` of its own, and it is a calendar date. So a client
 * generated from the old document read `response.day` expecting a string and
 * got an object, and the failure mode was a wrong render rather than an error.
 * The backend found three sibling envelope bugs while fixing ours; this was the
 * one they called the quietest, and it is the reason the transform unwraps
 * explicitly instead of anybody reaching for `response.day` by hand.
 */
const LoggedDayEnvelope = z.object({ day: ChallengeDay }).transform((response) => response.day);
export function useLogChallengeDay(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ day, note }) => http.post('/journey/challenge/log', { ...(day ? { day } : {}), ...(note ? { note } : {}) }, {
            schema: LoggedDayEnvelope,
            // `challenge_entries` is keyed on (user, day) so logging twice is one
            // entry — but the key still matters: a double-tap must not race.
            idempotencyKey: newIdempotencyKey(),
        }),
        /*
         * ONE day comes back, not the period — patch it in rather than replace.
         *
         * This used to parse the response as a whole `ChallengePeriod` and write it
         * straight over the cache. Against the real API that threw, so logging a day
         * failed outright; had it not thrown it would have been worse, because the
         * cache would have been replaced by a single day wearing the period's key.
         */
        onSuccess: (logged) => {
            queryClient.setQueryData(queryKeys.journey.challenge, (current) => current
                ? { ...current, days: current.days.map((d) => (d.day === logged.day ? logged : d)) }
                : current);
            // The streak feeds the journey score and the badge wall reads the same
            // journey, both computed on read.
            void queryClient.invalidateQueries({ queryKey: queryKeys.journey.root });
            void queryClient.invalidateQueries({ queryKey: queryKeys.journey.leaderboard });
        },
    });
}
/**
 * Your own past periods — the Challenge month view (design §10).
 *
 * Blocked for four rounds and correctly so: `GET /journey/challenge/periods`
 * exists but is `content.author`-gated and returns anonymous aggregates, which
 * is an administrator reading engagement rather than a parent reading their own
 * month. Widening it would have turned an aggregate into a roster.
 *
 * `mine`-shaped, as the backend predicted we would need: the caller never names
 * a resource, so there is no id anybody can substitute for somebody else's.
 *
 * Each period carries its own `days` **with the reflection**, so the month view
 * is one request rather than one per period — and `elapsedDays` is what makes a
 * live period's ring honest, since a week three days in is 3/3 rather than 3/7.
 */
export const MyChallengePeriod = z.object({
    id: z.string().uuid(),
    kind: z.enum(['WEEK', 'MONTH']),
    startsOn: z.string(),
    endsOn: z.string(),
    title: z.string(),
    /**
     * The week's description, which the server always sent and this schema
     * dropped — so a past week showed its daily prompt instead (Asana
     * 1219490946060697, where descriptions grew to 2 000 characters).
     */
    description: z.string().nullable().optional(),
    dailyPrompt: z.string().optional(),
    practices: z.array(z.string()),
    days: z.array(ChallengeDay),
    state: z.enum(['PAST', 'LIVE', 'UPCOMING']),
    archived: z.boolean(),
    /** Days of this period that have actually happened. See above. */
    elapsedDays: z.number().int().nonnegative(),
});
const MyChallengePeriodList = z.object({
    items: z.array(MyChallengePeriod),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
export function useMyChallengePeriods(http, options = {}) {
    const { kind, from, to, page = 1, locale } = options;
    return useQuery({
        queryKey: ['journey', 'challenge', 'mine', { kind, from, to, page, locale }],
        queryFn: () => http.get('/journey/challenge/periods/mine', {
            schema: MyChallengePeriodList,
            query: { kind, from, to, page, pageSize: 12 },
            ...(locale ? { locale } : {}),
        }),
    });
}
//# sourceMappingURL=challenge.js.map